/**
 * Controlled HTML for custom components (owner feedback F21, level A): the markup node renders an HTML template with
 * `{{ expression }}` bindings and a component-scoped stylesheet, without any script. This module is the whole trust
 * boundary and is pure (browser and server run the same code):
 *
 *   parseMarkup(html)       -> a tree of allow-listed tags and attributes; everything else is dropped and reported
 *   sanitizeStyle(decls)    -> the declarations of a `style` attribute that are allowed
 *   scopeCss(css, scope)    -> the author's stylesheet, scoped to one node, limited to safe properties and design
 *                              tokens (no `url()`, no `@import`, no literal colours, no `position: fixed`)
 *
 * The renderer builds React elements from the tree (never `innerHTML`), so what is not in the allow-lists cannot reach
 * the page: no `script`, no `on*` handler, no `javascript:` address, no form, no frame, no external resource.
 * Expression values are HTML-escaped before the markup is parsed, so data can never add a tag or an attribute.
 */

import { brandProblem, nearestColourToken } from './custom-brand';

export type MarkupIssue = {
  code: MarkupIssueCode;
  message: string;
  detail?: string;
  /** The design token to use instead (`accent`). */ token?: string;
};
export type MarkupIssueCode =
  | 'tag'
  | 'attribute'
  | 'handler'
  | 'url'
  | 'style'
  | 'css_property'
  | 'css_value'
  | 'css_rule'
  | 'css_selector'
  | 'token_position'
  | 'size';

export type MarkupNode =
  | { t: 'text'; text: string }
  | { t: 'el'; tag: string; attrs: Record<string, string>; children: MarkupNode[] };

export const MARKUP_LIMITS = { html: 24_000, css: 24_000, nodes: 1500, depth: 24 } as const;

// -- tags and attributes ---------------------------------------------------------------------------------

const HTML_TAGS = new Set(
  (
    'div span p h1 h2 h3 h4 h5 h6 ul ol li dl dt dd table thead tbody tfoot tr th td caption colgroup col a strong b em i u s small mark sub sup code pre kbd ' +
    'blockquote hr br figure figcaption section article aside header footer nav main details summary img time abbr cite q dfn var samp bdi wbr address meter progress'
  ).split(' '),
);
const SVG_TAGS = new Set(
  'svg g path circle rect line polyline polygon ellipse text tspan defs lineargradient radialgradient stop title desc'.split(
    ' ',
  ),
);
/** Elements that are removed together with everything inside them. */
const DROP_WITH_CONTENT = new Set(
  'script style iframe frame frameset object embed applet link meta base form input button textarea select option optgroup video audio source track canvas template noscript math foreignobject use image animate set animatetransform animatemotion'.split(
    ' ',
  ),
);
/** Elements that have no end tag. */
const VOID = new Set('br hr img col wbr area base embed input link meta param source track'.split(' '));

const SVG_CASE: Record<string, string> = {
  lineargradient: 'linearGradient',
  radialgradient: 'radialGradient',
};

const GLOBAL_ATTRS = new Set([
  'class',
  'id',
  'title',
  'lang',
  'dir',
  'role',
  'aria-label',
  'aria-hidden',
  'aria-describedby',
  'aria-labelledby',
  'aria-live',
  'aria-level',
  'style',
  'hidden',
  'translate',
]);
const TAG_ATTRS: Record<string, readonly string[]> = {
  a: ['href'],
  img: ['src', 'alt', 'width', 'height'],
  td: ['colspan', 'rowspan', 'headers'],
  th: ['colspan', 'rowspan', 'scope', 'headers'],
  col: ['span'],
  colgroup: ['span'],
  ol: ['start', 'reversed', 'type'],
  li: ['value'],
  details: ['open'],
  time: ['datetime'],
  meter: ['value', 'min', 'max', 'low', 'high', 'optimum'],
  progress: ['value', 'max'],
  abbr: [],
  q: [],
};
const SVG_ATTRS = new Set(
  (
    'viewbox width height fill stroke stroke-width stroke-linecap stroke-linejoin stroke-dasharray stroke-dashoffset stroke-opacity fill-opacity fill-rule clip-rule opacity d cx cy r rx ry x y x1 y1 x2 y2 points transform ' +
    'offset stop-color stop-opacity gradientunits gradienttransform preserveaspectratio text-anchor dominant-baseline font-size font-weight font-family letter-spacing dx dy xmlns'
  ).split(' '),
);
const ROLES = new Set(
  'list listitem img presentation none group region status heading article navigation note separator figure term definition'.split(
    ' ',
  ),
);
const SVG_PAINT = new Set(['fill', 'stroke', 'stop-color']);

const SAFE_ID = /^[A-Za-z][A-Za-z0-9_-]{0,40}$/;
const SAFE_CLASS = /^[A-Za-z_][A-Za-z0-9_-]{0,40}$/;
const DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp);base64,[A-Za-z0-9+/=]{1,200000}$/;
/** An image of the workspace library (`/api/v1/assets/<id>`): same origin, loaded through the app's own route, nothing external. */
const ASSET_IMAGE = /^\/api\/v1\/assets\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** An address inside the app, a fragment, a mail or a phone link: nothing that leaves the app or runs code. */
const SAFE_HREF =
  /^(\/(?!\/)[A-Za-z0-9\-._~/?=&%#:@+,]*|#[A-Za-z0-9\-._~]*|mailto:[^\s<>"']+|tel:[+0-9 ()\-.]+)$/;

// -- entities ----------------------------------------------------------------------------------------------

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  copy: '©',
  reg: '®',
  hellip: '…',
  mdash: '—',
  ndash: '–',
  middot: '·',
  bull: '•',
  euro: '€',
  times: '×',
  rarr: '→',
  larr: '←',
  check: '✓',
};

export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, body: string) => {
    if (body[0] === '#') {
      const code = body[1]!.toLowerCase() === 'x' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code > 8 && code < 0x10ffff && !(code >= 0xd800 && code <= 0xdfff)
        ? String.fromCodePoint(code)
        : '';
    }
    return ENTITIES[body.toLowerCase()] ?? m;
  });
}

/** An expression value as text that cannot add markup (used when a template is filled before it is parsed). */
export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

// -- the parser --------------------------------------------------------------------------------------------

const TOKEN_MARK = '';

/** Hide `{{ … }}` tokens (they may hold `<`, `>` and quotes) so the tokenizer never sees them. */
function maskTokens(html: string): { masked: string; tokens: string[] } {
  const tokens: string[] = [];
  const masked = html.replace(/\{\{[\s\S]*?\}\}/g, (m) => {
    tokens.push(m);
    return `${TOKEN_MARK}${tokens.length - 1}${TOKEN_MARK}`;
  });
  return { masked, tokens };
}
const unmask = (s: string, tokens: string[]): string =>
  s.replace(new RegExp(`${TOKEN_MARK}(\\d+)${TOKEN_MARK}`, 'g'), (_m, i: string) => tokens[Number(i)] ?? '');

/** Parse markup into the allow-listed tree. Issues are listed once per kind of problem and place. */
export function parseMarkup(html: string): { nodes: MarkupNode[]; issues: MarkupIssue[] } {
  const issues: MarkupIssue[] = [];
  const seen = new Set<string>();
  const report = (code: MarkupIssueCode, message: string, detail?: string) => {
    const key = `${code}:${detail ?? message}`;
    if (seen.has(key) || issues.length >= 40) return;
    seen.add(key);
    issues.push({ code, message, ...(detail ? { detail } : {}) });
  };
  if (html.length > MARKUP_LIMITS.html) {
    report('size', `The markup is longer than ${MARKUP_LIMITS.html} characters`);
    html = html.slice(0, MARKUP_LIMITS.html);
  }
  const { masked, tokens } = maskTokens(html);
  const root: MarkupNode[] = [];
  const stack: Array<{ tag: string; children: MarkupNode[]; skip: boolean; dropTo?: string }> = [
    { tag: '#root', children: root, skip: false },
  ];
  let count = 0;
  let svgDepth = 0;
  let i = 0;
  const n = masked.length;
  const top = () => stack[stack.length - 1]!;
  const pushText = (raw: string) => {
    if (!raw) return;
    const cur = top();
    if (cur.skip) return;
    const text = unmask(decodeEntities(raw), tokens);
    if (!text) return;
    const last = cur.children[cur.children.length - 1];
    if (last?.t === 'text') last.text += text;
    else cur.children.push({ t: 'text', text });
  };
  const nameOk = (s: string) => /^[A-Za-z][A-Za-z0-9-]*$/.test(s);

  while (i < n) {
    const lt = masked.indexOf('<', i);
    if (lt === -1) {
      pushText(masked.slice(i));
      break;
    }
    if (lt > i) pushText(masked.slice(i, lt));
    i = lt;
    // comments and doctype
    if (masked.startsWith('<!--', i)) {
      const end = masked.indexOf('-->', i + 4);
      i = end === -1 ? n : end + 3;
      continue;
    }
    if (masked[i + 1] === '!' || masked[i + 1] === '?') {
      const end = masked.indexOf('>', i);
      i = end === -1 ? n : end + 1;
      continue;
    }
    // end tag
    if (masked[i + 1] === '/') {
      const end = masked.indexOf('>', i);
      const name = masked
        .slice(i + 2, end === -1 ? n : end)
        .trim()
        .toLowerCase();
      i = end === -1 ? n : end + 1;
      // close the nearest open element of that name
      for (let s = stack.length - 1; s > 0; s--) {
        if (stack[s]!.tag === name) {
          const closing = stack.splice(s);
          for (const c of closing) if (c.tag === 'svg') svgDepth = Math.max(0, svgDepth - 1);
          break;
        }
      }
      continue;
    }
    // start tag
    const m = /^<([A-Za-z][A-Za-z0-9-]*)/.exec(masked.slice(i, i + 40));
    if (!m) {
      pushText('<');
      i += 1;
      continue;
    }
    const rawName = m[1]!;
    const tag = rawName.toLowerCase();
    let j = i + m[0].length;
    const attrs: Array<[string, string | null]> = [];
    let selfClose = false;
    // attributes
    for (;;) {
      while (j < n && /\s/.test(masked[j]!)) j++;
      if (j >= n) break;
      const c = masked[j]!;
      if (c === '>') {
        j++;
        break;
      }
      if (c === '/' && masked[j + 1] === '>') {
        selfClose = true;
        j += 2;
        break;
      }
      if (c === '/') {
        j++;
        continue;
      }
      let k = j;
      while (k < n && !/[\s=>/]/.test(masked[k]!)) k++;
      const name = masked.slice(j, k);
      j = k;
      while (j < n && /\s/.test(masked[j]!)) j++;
      let value: string | null = null;
      if (masked[j] === '=') {
        j++;
        while (j < n && /\s/.test(masked[j]!)) j++;
        const q = masked[j];
        if (q === '"' || q === "'") {
          const end = masked.indexOf(q, j + 1);
          value = masked.slice(j + 1, end === -1 ? n : end);
          j = end === -1 ? n : end + 1;
        } else {
          let e = j;
          while (e < n && !/[\s>]/.test(masked[e]!)) e++;
          value = masked.slice(j, e);
          j = e;
        }
      }
      if (name) attrs.push([name, value]);
    }
    i = j;

    const parent = top();
    const inSvg = svgDepth > 0;
    if (DROP_WITH_CONTENT.has(tag)) {
      report('tag', `<${tag}> is not allowed`, tag);
      if (!selfClose && !VOID.has(tag)) {
        // skip until the matching end tag
        const close = new RegExp(`</${tag}\\s*>`, 'i').exec(masked.slice(i));
        i = close ? i + close.index + close[0].length : n;
      }
      continue;
    }
    const allowed = inSvg ? SVG_TAGS.has(tag) || tag === 'svg' : HTML_TAGS.has(tag) || tag === 'svg';
    if (!allowed || !nameOk(rawName)) {
      report('tag', `<${tag}> is not allowed`, tag);
      // unwrap: keep the children of an unknown element
      if (!selfClose && !VOID.has(tag)) stack.push({ tag, children: parent.children, skip: parent.skip });
      continue;
    }
    count++;
    if (count > MARKUP_LIMITS.nodes) {
      report('size', `At most ${MARKUP_LIMITS.nodes} elements`);
      break;
    }
    if (stack.length > MARKUP_LIMITS.depth) {
      report('size', `At most ${MARKUP_LIMITS.depth} levels deep`);
      break;
    }
    const clean: Record<string, string> = {};
    const isSvgEl = tag === 'svg' || inSvg;
    for (const [rawAttr, rawValue] of attrs) {
      const name = rawAttr.toLowerCase();
      if (/^on/i.test(name)) {
        report('handler', `Event handlers such as "${name}" are not allowed`, name);
        continue;
      }
      if (rawAttr.includes(TOKEN_MARK)) {
        report('token_position', 'Expressions may only be used in texts and attribute values', rawAttr);
        continue;
      }
      const value = rawValue === null ? '' : unmask(decodeEntities(rawValue), tokens);
      const hasToken = value.includes('{{');
      if (name === 'style') {
        clean.style = value;
        continue;
      }
      const okHtml =
        !isSvgEl &&
        (GLOBAL_ATTRS.has(name) || TAG_ATTRS[tag]?.includes(name) || /^data-[a-z0-9-]{1,40}$/.test(name));
      const okSvg =
        isSvgEl &&
        (SVG_ATTRS.has(name) || ['class', 'id', 'role', 'aria-label', 'aria-hidden', 'style'].includes(name));
      if (!okHtml && !okSvg) {
        report('attribute', `The attribute "${name}" is not allowed on <${tag}>`, `${tag}.${name}`);
        continue;
      }
      if (name === 'href') {
        if (!hasToken && !SAFE_HREF.test(value.trim())) {
          report(
            'url',
            'Links may only point inside the app (/path), to #anchors, mail or phone',
            value.slice(0, 60),
          );
          continue;
        }
      } else if (name === 'src') {
        if (!hasToken && !DATA_IMAGE.test(value.trim()) && !ASSET_IMAGE.test(value.trim())) {
          report(
            'url',
            'Images must be library images (/api/v1/assets/<id>) or data:image URLs: nothing is loaded from outside',
            value.slice(0, 40),
          );
          continue;
        }
      } else if (name === 'role' && !hasToken && !ROLES.has(value)) {
        report('attribute', `The role "${value}" is not allowed`, `role.${value}`);
        continue;
      } else if (name === 'id' && !hasToken && !SAFE_ID.test(value)) {
        report('attribute', 'An id may use letters, digits, - and _', value.slice(0, 30));
        continue;
      } else if (
        name === 'class' &&
        !hasToken &&
        !value
          .split(/\s+/)
          .filter(Boolean)
          .every((c) => SAFE_CLASS.test(c))
      ) {
        report('attribute', 'A class name may use letters, digits, - and _', value.slice(0, 30));
        continue;
      } else if (SVG_PAINT.has(name) && !hasToken && !isPaintOk(value)) {
        report(
          'css_value',
          `The ${name} of a shape must be a design token such as var(--accent), currentColor or none`,
          value.slice(0, 30),
        );
        continue;
      } else if (name === 'xmlns') {
        continue;
      }
      clean[name] = value;
    }
    if (tag === 'a') clean.rel = 'noopener noreferrer';
    const el: MarkupNode = { t: 'el', tag: SVG_CASE[tag] ?? tag, attrs: clean, children: [] };
    if (!parent.skip) parent.children.push(el);
    if (tag === 'svg') svgDepth++;
    if (!selfClose && !VOID.has(tag)) stack.push({ tag, children: el.children, skip: parent.skip });
    else if (tag === 'svg') svgDepth = Math.max(0, svgDepth - 1);
  }
  return { nodes: root, issues };
}

// -- css values --------------------------------------------------------------------------------------------

/** The design tokens a stylesheet or markup may read (`var(--ink)`). Everything else (`--my-var`) is the author's own. */
export const MARKUP_TOKENS = [
  'ink',
  'ink-2',
  'ink-3',
  'ink-4',
  'ink-strong',
  'border',
  'border-strong',
  'border-subtle',
  'canvas',
  'surface',
  'subtle',
  'neutral-chip',
  'accent',
  'accent-hover',
  'accent-ink',
  'accent-tint',
  'accent-border',
  'accent-soft',
  'on-accent',
  'on-strong',
  'focus-ring',
  'info',
  'info-bg',
  'info-border',
  'ok',
  'ok-bg',
  'ok-border',
  'ok-strong',
  'warn',
  'warn-bg',
  'warn-border',
  'warn-ink',
  'warn-strong',
  'err',
  'err-bg',
  'err-border',
  'err-strong',
  'ext',
  'ext-bg',
  'comp',
  'comp-bg',
  'inverse',
  'on-inverse',
  'on-inverse-muted',
  'paper',
  'obj-indigo',
  'obj-indigo-bg',
  'obj-blue',
  'obj-blue-bg',
  'obj-teal',
  'obj-teal-bg',
  'obj-green',
  'obj-green-bg',
  'obj-amber',
  'obj-amber-bg',
  'obj-orange',
  'obj-orange-bg',
  'obj-rose',
  'obj-rose-bg',
  'obj-violet',
  'obj-violet-bg',
  'obj-slate',
  'obj-slate-bg',
  'shadow-xs',
  'shadow-card',
  'shadow-raised',
  'shadow-pop',
  'hairline',
  'hairline-soft',
  'hover-tint',
  'selection-tint',
  'radius-sm',
  'radius',
  'radius-card',
  'radius-modal',
  'radius-sheet',
  'radius-pill',
  'font-sans',
  'font-mono',
  'font-heading',
  'text-11',
  'text-12',
  'text-13',
  'text-14',
  'text-16',
  'text-20',
  'text-24',
  'text-32',
  'dur-fast',
  'dur-base',
  'dur-slow',
  'ease-out',
  'ease-in',
] as const;
const TOKEN_SET = new Set<string>(MARKUP_TOKENS);

const NAMED_COLORS = new Set(
  'aliceblue antiquewhite aqua aquamarine azure beige bisque black blanchedalmond blue blueviolet brown burlywood cadetblue chartreuse chocolate coral cornflowerblue cornsilk crimson cyan darkblue darkcyan darkgoldenrod darkgray darkgreen darkgrey darkkhaki darkmagenta darkolivegreen darkorange darkorchid darkred darksalmon darkseagreen darkslateblue darkslategray darkslategrey darkturquoise darkviolet deeppink deepskyblue dimgray dimgrey dodgerblue firebrick floralwhite forestgreen fuchsia gainsboro ghostwhite gold goldenrod gray green greenyellow grey honeydew hotpink indianred indigo ivory khaki lavender lavenderblush lawngreen lemonchiffon lightblue lightcoral lightcyan lightgoldenrodyellow lightgray lightgreen lightgrey lightpink lightsalmon lightseagreen lightskyblue lightslategray lightslategrey lightsteelblue lightyellow lime limegreen linen magenta maroon mediumaquamarine mediumblue mediumorchid mediumpurple mediumseagreen mediumslateblue mediumspringgreen mediumturquoise mediumvioletred midnightblue mintcream mistyrose moccasin navajowhite navy oldlace olive olivedrab orange orangered orchid palegoldenrod palegreen paleturquoise palevioletred papayawhip peachpuff peru pink plum powderblue purple rebeccapurple red rosybrown royalblue saddlebrown salmon sandybrown seagreen seashell sienna silver skyblue slateblue slategray slategrey snow springgreen steelblue tan teal thistle tomato turquoise violet wheat white whitesmoke yellow yellowgreen canvas canvastext highlight highlighttext buttonface buttontext linktext'.split(
    ' ',
  ),
);

function isPaintOk(v: string): boolean {
  const s = v.trim().toLowerCase();
  if (['none', 'currentcolor', 'transparent', 'inherit'].includes(s)) return true;
  return valueProblem('color', v) === null;
}

/** The message of a literal colour: the nearest design token when there is one. */
function colourMessage(literal: string): string {
  const token = nearestColourToken(literal);
  return token
    ? `use var(--${token}) instead of ${literal} (a design token follows the brand)`
    : 'use a design token such as var(--accent) instead of a colour value';
}

/** Why a CSS value is not allowed (null: it is). */
function valueProblem(prop: string, raw: string): string | null {
  const v = raw.trim();
  if (!v) return 'empty';
  if (v.length > 400) return 'too long';
  const low = v.toLowerCase();
  if (/[<>\\@;{}]/.test(v) || /\/\*|\*\//.test(v)) return 'it holds a character that is not allowed';
  if (
    /url\s*\(|image-set\s*\(|expression\s*\(|element\s*\(|javascript:|data:|behavior|binding|!important/.test(
      low,
    )
  )
    return 'urls, expressions and !important are not allowed';
  const hex = /#[0-9a-f]{3,8}\b/i.exec(v);
  if (hex) return colourMessage(hex[0]);
  const fn = /\b(rgba?|hsla?|hwb|lab|lch|oklab|oklch|color|device-cmyk)\s*\([^)]*\)/i.exec(v);
  if (fn) return colourMessage(fn[0]);
  // every function must be one of the allowed ones
  for (const f of low.matchAll(/([a-z-]+)\s*\(/g)) {
    const name = f[1]!;
    if (!ALLOWED_FUNCTIONS.has(name)) return `the function ${name}() is not allowed`;
  }
  // var() may read design tokens and the author's own custom properties
  for (const x of low.matchAll(/var\(\s*--([a-z0-9-]+)/g)) {
    const t = x[1]!;
    if (!TOKEN_SET.has(t) && !/^[a-z][a-z0-9-]*$/.test(t)) return 'unknown variable';
  }
  // no literal colour names (strip var(...) names and strings first)
  const bare = low
    .replace(/var\(\s*--[a-z0-9-]+\s*(,[^)]*)?\)/g, ' ')
    .replace(/"[^"]*"|'[^']*'/g, ' ')
    .replace(/[a-z-]+\s*\(/g, ' ');
  for (const word of bare.match(/[a-z][a-z-]*/g) ?? [])
    if (NAMED_COLORS.has(word) && !(prop === 'font-family' || prop === 'font')) return colourMessage(word);
  return null;
}
const ALLOWED_FUNCTIONS = new Set(
  'var calc min max clamp translate translatex translatey translatez translate3d scale scalex scaley scale3d rotate rotatex rotatey rotatez skew skewx skewy matrix perspective linear-gradient radial-gradient conic-gradient repeating-linear-gradient repeating-radial-gradient color-mix blur brightness contrast grayscale hue-rotate invert saturate sepia drop-shadow opacity polygon circle ellipse inset cubic-bezier steps minmax repeat fit-content counter counters format attr'.split(
    ' ',
  ),
);

// -- properties --------------------------------------------------------------------------------------------

const PROPERTY_PREFIXES = [
  'margin',
  'padding',
  'border',
  'outline',
  'grid',
  'flex',
  'gap',
  'row-gap',
  'column-gap',
  'align',
  'justify',
  'place',
  'font',
  'text',
  'white-space',
  'word',
  'line',
  'letter',
  'list-style',
  'animation',
  'transition',
  'transform',
  'inset',
  'overflow',
  'scroll-snap',
  'background',
  'min-',
  'max-',
];
const PROPERTIES = new Set(
  'display position top right bottom left z-index float clear width height aspect-ratio box-sizing object-fit object-position order color opacity cursor visibility pointer-events user-select box-shadow filter backdrop-filter mix-blend-mode clip-path content counter-reset counter-increment columns column-count column-gap vertical-align hyphens overflow-wrap tab-size isolation caret-color accent-color text-decoration text-transform text-indent text-overflow text-shadow text-align letter-spacing line-height word-spacing word-break white-space fill stroke stroke-width stroke-linecap stroke-linejoin stroke-dasharray stroke-dashoffset'.split(
    ' ',
  ),
);
const BAD_PREFIX_PROPS = new Set(['list-style-image', 'border-image', 'border-image-source']);

function propertyAllowed(name: string): boolean {
  if (BAD_PREFIX_PROPS.has(name)) return false;
  if (name.startsWith('--')) return /^--[a-z][a-z0-9-]{0,40}$/.test(name);
  if (PROPERTIES.has(name)) return true;
  return PROPERTY_PREFIXES.some((p) => name === p || name.startsWith(p.endsWith('-') ? p : `${p}-`));
}

/** One declaration; `names` are the keyframes of the stylesheet (animation names are scoped to the node). */
function checkDeclaration(
  name: string,
  value: string,
  scope: string | null,
  frames: ReadonlySet<string>,
): { value: string } | { problem: MarkupIssue } {
  const prop = name.toLowerCase().trim();
  if (!propertyAllowed(prop))
    return {
      problem: { code: 'css_property', message: `The property "${prop}" is not allowed`, detail: prop },
    };
  let v = value.trim();
  const problem = valueProblem(prop, v);
  if (problem) {
    const token = /use var\(--([a-z0-9-]+)\)/.exec(problem)?.[1];
    return {
      problem: {
        code: 'css_value',
        message: problem.startsWith('use var(') ? `${prop}: ${problem}` : `${prop}: ${problem}`,
        detail: `${prop}: ${v.slice(0, 40)}`,
        ...(token ? { token } : {}),
      },
    };
  }
  const brand = brandProblem(prop, v);
  if (brand)
    return {
      problem: {
        code: 'css_value',
        message: brand.message,
        detail: `${prop}: ${v.slice(0, 40)}`,
        ...(brand.token ? { token: brand.token } : {}),
      },
    };
  const low = v.toLowerCase();
  if (
    prop === 'position' &&
    !['static', 'relative', 'absolute', 'sticky', 'inherit', 'initial', 'unset'].includes(low)
  )
    return {
      problem: {
        code: 'css_value',
        message: 'position: fixed is not allowed: components stay inside their box',
        detail: `position: ${low}`,
      },
    };
  if (prop === 'z-index') {
    const z = Number(v);
    if (!Number.isInteger(z) || z < -1 || z > 10)
      return {
        problem: {
          code: 'css_value',
          message: 'z-index may be -1 to 10: components never cover the app',
          detail: `z-index: ${v}`,
        },
      };
  }
  if (
    prop === 'content' &&
    !/^("[^"]{0,80}"|'[^']{0,80}'|none|normal|counter\([^)]*\)|""|'')(\s+("[^"]{0,80}"|'[^']{0,80}'))*$/.test(
      v,
    )
  )
    return { problem: { code: 'css_value', message: 'content may only be a text', detail: v.slice(0, 30) } };
  if (prop === 'animation' || prop === 'animation-name') {
    // scope the names of our own keyframes; any other name does nothing
    v = v.replace(/-?[a-z_][a-z0-9_-]*/gi, (w) => (scope && frames.has(w) ? `${scope}-${w}` : w));
  }
  return { value: v };
}

/** The allowed declarations of a `style` attribute, as an object with CSS property names. */
export function sanitizeStyle(style: string): { style: Record<string, string>; issues: MarkupIssue[] } {
  const out: Record<string, string> = {};
  const issues: MarkupIssue[] = [];
  for (const d of splitDeclarations(style)) {
    const r = checkDeclaration(d.name, d.value, null, new Set());
    if ('problem' in r) issues.push(r.problem);
    else out[d.name.toLowerCase().trim()] = r.value;
  }
  return { style: out, issues };
}

function splitDeclarations(body: string): Array<{ name: string; value: string }> {
  const out: Array<{ name: string; value: string }> = [];
  let depth = 0;
  let quote = '';
  let cur = '';
  const parts: string[] = [];
  for (const ch of body) {
    if (quote) {
      cur += ch;
      if (ch === quote) quote = '';
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      cur += ch;
    } else if (ch === '(') {
      depth++;
      cur += ch;
    } else if (ch === ')') {
      depth = Math.max(0, depth - 1);
      cur += ch;
    } else if (ch === ';' && depth === 0) {
      parts.push(cur);
      cur = '';
    } else cur += ch;
  }
  parts.push(cur);
  for (const part of parts) {
    const idx = part.indexOf(':');
    if (idx <= 0) continue;
    const name = part.slice(0, idx).trim();
    const value = part.slice(idx + 1).trim();
    if (name && value) out.push({ name, value });
  }
  return out;
}

// -- the stylesheet ----------------------------------------------------------------------------------------

const PSEUDO_CLASSES = new Set(
  'hover focus focus-visible focus-within active first-child last-child only-child first-of-type last-of-type only-of-type empty checked disabled scope'.split(
    ' ',
  ),
);
const PSEUDO_FUNCTIONS = new Set([
  'nth-child',
  'nth-last-child',
  'nth-of-type',
  'nth-last-of-type',
  'not',
  'is',
  'where',
]);
const PSEUDO_ELEMENTS = new Set(['before', 'after', 'first-line', 'first-letter', 'marker']);

/** The class a node's markup gets in the page: author classes are prefixed so they never meet the app's own. */
export const scopedClass = (scope: string, name: string): string => `mk-${scope}-${name}`;

function scopeSelector(sel: string, scope: string, issues: MarkupIssue[], rootClass: string): string | null {
  const s = sel.trim();
  if (!s || s.length > 200) return null;
  if (/[<>{}\\]/.test(s.replace(/>/g, '')) || /\/\*|url\(|@/.test(s)) {
    issues.push({ code: 'css_selector', message: 'This selector is not allowed', detail: s.slice(0, 40) });
    return null;
  }
  const root = `.${rootClass}[data-mk="${scope}"]`;
  let out = '';
  let i = 0;
  let expectCompound = true;
  while (i < s.length) {
    const c = s[i]!;
    if (/\s/.test(c)) {
      i++;
      if (!expectCompound && !/^\s*[>+~]/.test(s.slice(i))) {
        out += ' ';
        expectCompound = true;
      }
      continue;
    }
    if (c === '>' || c === '+' || c === '~') {
      out = out.trimEnd() + ` ${c} `;
      i++;
      expectCompound = true;
      continue;
    }
    // a compound selector
    let compound = '';
    let tagDone = false;
    while (i < s.length && !/[\s>+~,]/.test(s[i]!)) {
      const ch = s[i]!;
      if (ch === '.') {
        const m = /^\.([A-Za-z_][A-Za-z0-9_-]*)/.exec(s.slice(i));
        if (!m) return fail(issues, s);
        compound += `.${scopedClass(scope, m[1]!)}`;
        i += m[0].length;
      } else if (ch === '#') {
        const m = /^#([A-Za-z][A-Za-z0-9_-]*)/.exec(s.slice(i));
        if (!m) return fail(issues, s);
        compound += `#${scopedClass(scope, m[1]!)}`;
        i += m[0].length;
      } else if (ch === '[') {
        const m =
          /^\[\s*(data-[a-z0-9-]+|class|role|open|aria-[a-z-]+)\s*(?:([~|^$*]?=)\s*("[^"]*"|'[^']*'|[A-Za-z0-9_-]+))?\s*\]/.exec(
            s.slice(i),
          );
        if (!m) return fail(issues, s);
        compound += m[0];
        i += m[0].length;
      } else if (ch === ':') {
        const dbl = s[i + 1] === ':';
        const m = /^::?([a-z-]+)/.exec(s.slice(i));
        if (!m) return fail(issues, s);
        const name = m[1]!;
        i += m[0].length;
        if (dbl || PSEUDO_ELEMENTS.has(name)) {
          if (!PSEUDO_ELEMENTS.has(name)) return fail(issues, s);
          compound += `::${name}`;
        } else if (s[i] === '(') {
          let depth = 0;
          let k = i;
          for (; k < s.length; k++) {
            if (s[k] === '(') depth++;
            else if (s[k] === ')' && --depth === 0) break;
          }
          const arg = s.slice(i + 1, k);
          if (!PSEUDO_FUNCTIONS.has(name)) return fail(issues, s);
          if (name.startsWith('nth')) {
            if (!/^\s*(odd|even|[+-]?\d*n?\s*([+-]\s*\d+)?)\s*$/.test(arg)) return fail(issues, s);
            compound += `:${name}(${arg.trim()})`;
          } else {
            const inner = arg
              .split(',')
              .map((x) => scopeSelector(x, scope, issues, rootClass))
              .filter((x): x is string => !!x)
              .map((x) => x.replace(root, '').trim());
            if (!inner.length) return fail(issues, s);
            compound += `:${name}(${inner.join(', ')})`;
          }
          i = k + 1;
        } else if (name === 'scope') {
          compound += '';
          tagDone = true;
        } else if (PSEUDO_CLASSES.has(name)) compound += `:${name}`;
        else return fail(issues, s);
      } else if (ch === '*') {
        compound += '*';
        i++;
      } else {
        const m = /^[A-Za-z][A-Za-z0-9-]*/.exec(s.slice(i));
        if (!m || tagDone || compound) return fail(issues, s);
        const tag = m[0].toLowerCase();
        if (!HTML_TAGS.has(tag) && !SVG_TAGS.has(tag)) return fail(issues, s);
        compound += tag;
        i += m[0].length;
      }
    }
    if (expectCompound && out === '') out = root + (compound ? ' ' : '');
    out += compound || '';
    if (!compound && out === root + ' ') out = root;
    expectCompound = false;
  }
  return out || root;
}
function fail(issues: MarkupIssue[], s: string): null {
  issues.push({ code: 'css_selector', message: 'This selector is not allowed', detail: s.slice(0, 40) });
  return null;
}

type Block = { prelude: string; body: string };

/** Split top-level rules (`prelude { body }`), honouring quotes and nested braces. */
function topLevel(css: string): Block[] {
  const out: Block[] = [];
  let i = 0;
  const n = css.length;
  while (i < n) {
    let j = i;
    let quote = '';
    while (j < n && (quote || css[j] !== '{')) {
      if (quote) {
        if (css[j] === quote) quote = '';
      } else if (css[j] === '"' || css[j] === "'") quote = css[j]!;
      else if (css[j] === ';') {
        // a statement at-rule such as @import …;
        out.push({ prelude: css.slice(i, j).trim(), body: '' });
        i = j + 1;
        j = i;
        continue;
      }
      j++;
    }
    if (j >= n) break;
    const prelude = css.slice(i, j).trim();
    let depth = 0;
    let k = j;
    quote = '';
    for (; k < n; k++) {
      const c = css[k]!;
      if (quote) {
        if (c === quote) quote = '';
        continue;
      }
      if (c === '"' || c === "'") quote = c;
      else if (c === '{') depth++;
      else if (c === '}' && --depth === 0) break;
    }
    out.push({ prelude, body: css.slice(j + 1, k) });
    i = k + 1;
  }
  return out;
}

const LEN = String.raw`\d{1,4}(?:\.\d+)?(?:px|em|rem)`;
const FEATURES: RegExp[] = [
  new RegExp(String.raw`^\(\s*(?:min|max)-(?:width|height)\s*:\s*${LEN}\s*\)$`, 'i'),
  new RegExp(String.raw`^\(\s*(?:width|height)\s*(?:<=|>=|<|>|=)\s*${LEN}\s*\)$`, 'i'),
  new RegExp(String.raw`^\(\s*${LEN}\s*(?:<=|<)\s*(?:width|height)\s*(?:(?:<=|<)\s*${LEN})?\s*\)$`, 'i'),
  /^\(\s*prefers-reduced-motion\s*:\s*(?:reduce|no-preference)\s*\)$/i,
  /^\(\s*prefers-color-scheme\s*:\s*(?:light|dark)\s*\)$/i,
  /^\(\s*orientation\s*:\s*(?:portrait|landscape)\s*\)$/i,
];
/**
 * A media or container query that may stay: width and height limits (min-/max- or range syntax), prefers-reduced-motion,
 * prefers-color-scheme and orientation, joined with `and` (at most four), optionally led by `screen` or `all`. Nothing
 * else (no `print`, resolution, pointer or custom queries, no `or`/`not`, no commas).
 */
export function mediaQueryAllowed(query: string): boolean {
  const q = query
    .trim()
    .replace(/^(?:only\s+)?(?:screen|all)\s+and\s+/i, '')
    .replace(/^(?:only\s+)?(?:screen|all)$/i, '');
  if (!q) return true;
  const parts = q.split(/\s+and\s+|(?<=\))\s*and\s*(?=\()/i).map((x) => x.trim());
  return parts.length <= 4 && parts.every((x) => FEATURES.some((f) => f.test(x)));
}

/**
 * Scope an author's stylesheet to one node: every rule gets the node's root in front of it, only allowed properties
 * and values stay, animation names are prefixed. Returns the CSS to put in the page and what was dropped.
 */
export function scopeCss(
  css: string,
  scope: string,
  /** The class of the element that carries `data-mk` (the component library's `cc-markup`, a slide's `dr-markup`). */
  rootClass = 'cc-markup',
): { css: string; issues: MarkupIssue[] } {
  const issues: MarkupIssue[] = [];
  if (css.length > MARKUP_LIMITS.css) {
    issues.push({ code: 'size', message: `The stylesheet is longer than ${MARKUP_LIMITS.css} characters` });
    css = css.slice(0, MARKUP_LIMITS.css);
  }
  css = css.replace(/\/\*[\s\S]*?\*\//g, '');
  const blocks = topLevel(css);
  // keyframe names first, so animation declarations can be scoped
  const frames = new Set<string>();
  for (const b of blocks) {
    const m = /^@keyframes\s+([A-Za-z_][A-Za-z0-9_-]*)$/.exec(b.prelude);
    if (m) frames.add(m[1]!);
  }
  const out: string[] = [];
  const declarations = (body: string): string => {
    const lines: string[] = [];
    for (const d of splitDeclarations(body)) {
      const r = checkDeclaration(d.name, d.value, `mk-${scope}`, frames);
      if ('problem' in r) issues.push(r.problem);
      else lines.push(`${d.name.toLowerCase().trim()}: ${r.value};`);
    }
    return lines.join(' ');
  };
  const rule = (b: Block, nested = false): string[] => {
    const res: string[] = [];
    const prelude = b.prelude;
    if (prelude.startsWith('@')) {
      const kf = /^@keyframes\s+([A-Za-z_][A-Za-z0-9_-]*)$/.exec(prelude);
      if (kf) {
        const steps: string[] = [];
        for (const step of topLevel(b.body)) {
          if (!/^(from|to|\d{1,3}(\.\d+)?%)(\s*,\s*(from|to|\d{1,3}(\.\d+)?%))*$/.test(step.prelude)) {
            issues.push({
              code: 'css_rule',
              message: 'A keyframe step must be from, to or a percentage',
              detail: step.prelude.slice(0, 30),
            });
            continue;
          }
          steps.push(`${step.prelude} { ${declarations(step.body)} }`);
        }
        res.push(`@keyframes mk-${scope}-${kf[1]} { ${steps.join(' ')} }`);
        return res;
      }
      const media = /^@(media|container)(?=[\s(])\s*(.+)$/.exec(prelude);
      if (media && !nested) {
        const query = media[2]!.replace(/^\s*cc\s+/i, '').trim();
        if (!mediaQueryAllowed(query)) {
          issues.push({
            code: 'css_rule',
            message: `The query "${query.slice(0, 40)}" is not allowed (width and height limits, prefers-reduced-motion, prefers-color-scheme and orientation only)`,
            detail: query.slice(0, 40),
          });
          return res;
        }
        const inner = topLevel(b.body).flatMap((x) => rule(x, true));
        if (inner.length)
          res.push(`@${media[1]} ${media[1] === 'container' ? 'cc ' : ''}${query} { ${inner.join(' ')} }`);
        return res;
      }
      issues.push({
        code: 'css_rule',
        message: `${prelude.split(/\s/)[0]} is not allowed`,
        detail: prelude.slice(0, 30),
      });
      return res;
    }
    const selectors = prelude
      .split(',')
      .map((s) => scopeSelector(s, scope, issues, rootClass))
      .filter((s): s is string => !!s);
    if (!selectors.length) return res;
    const body = declarations(b.body);
    if (body) res.push(`${selectors.join(', ')} { ${body} }`);
    return res;
  };
  for (const b of blocks) out.push(...rule(b));
  return { css: out.join('\n'), issues };
}

// -- checking a node's markup -------------------------------------------------------------------------------

/** Every issue of a markup node's html (one text per language) and css: what the builder and the server report. */
export function markupIssues(html: string, css: string): MarkupIssue[] {
  const issues: MarkupIssue[] = [];
  const parsed = parseMarkup(html);
  issues.push(...parsed.issues);
  const visit = (nodes: MarkupNode[]) => {
    for (const n of nodes) {
      if (n.t !== 'el') continue;
      if (n.attrs.style !== undefined && !n.attrs.style.includes('{{'))
        issues.push(...sanitizeStyle(n.attrs.style).issues);
      visit(n.children);
    }
  };
  visit(parsed.nodes);
  issues.push(...scopeCss(css, 'check').issues);
  const seen = new Set<string>();
  return issues.filter((i) => {
    const k = `${i.code}:${i.detail ?? i.message}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** An issue in a language (the messages above are English; the builder and the server say them in the reader's language). */
export function markupIssueText(issue: Pick<MarkupIssue, 'message'>, locale: string): string {
  const m = issue.message;
  if (!locale.startsWith('de')) return m;
  let r: RegExpExecArray | null;
  if ((r = /^<(.+)> is not allowed$/.exec(m))) return `<${r[1]}> ist nicht erlaubt`;
  if ((r = /^The attribute "(.+)" is not allowed on <(.+)>$/.exec(m)))
    return `Das Attribut „${r[1]}“ ist bei <${r[2]}> nicht erlaubt`;
  if ((r = /^Event handlers such as "(.+)" are not allowed$/.exec(m)))
    return `Ereignis-Handler wie „${r[1]}“ sind nicht erlaubt`;
  if (m.startsWith('Links may only point inside the app'))
    return 'Links dürfen nur innerhalb der App, zu #Ankern, zu Mail oder Telefon führen';
  if (m.startsWith('Images must be data:image URLs'))
    return 'Bilder müssen data:image-Adressen sein: von außen wird nichts geladen';
  if ((r = /^The role "(.+)" is not allowed$/.exec(m))) return `Die Rolle „${r[1]}“ ist nicht erlaubt`;
  if (m.startsWith('An id may use')) return 'Eine ID darf Buchstaben, Ziffern, - und _ enthalten';
  if (m.startsWith('A class name may use'))
    return 'Ein Klassenname darf Buchstaben, Ziffern, - und _ enthalten';
  if (m.startsWith('Expressions may only be used'))
    return 'Ausdrücke sind nur in Texten und Attributwerten erlaubt';
  if ((r = /^The (fill|stroke|stop-color) of a shape must be/.exec(m)))
    return `Die Farbe (${r[1]}) einer Form muss ein Design-Token wie var(--accent), currentColor oder none sein`;
  if ((r = /^The property "(.+)" is not allowed$/.exec(m)))
    return `Die CSS-Eigenschaft „${r[1]}“ ist nicht erlaubt`;
  if (m.startsWith('position: fixed'))
    return 'position: fixed ist nicht erlaubt: Komponenten bleiben in ihrem Kasten';
  if (m.startsWith('z-index may be')) return 'z-index darf -1 bis 10 sein: Komponenten verdecken nie die App';
  if (
    (r = /^(.+?): use var\(--([a-z0-9-]+)\) instead of (.+?)( \(a design token follows the brand\))?$/.exec(
      m,
    ))
  )
    return `${r[1]}: Verwenden Sie var(--${r[2]}) statt ${r[3]}${r[4] ? ' (ein Design-Token folgt der Marke)' : ''}`;
  if ((r = /^(.+?): use var\(--([a-z0-9-]+)\), the workspace's brand font, instead of a font name$/.exec(m)))
    return `${r[1]}: Verwenden Sie var(--${r[2]}), die Markenschrift des Arbeitsbereichs, statt eines Schriftnamens`;
  if (m.includes('design token such as var(--accent)'))
    return m.replace(
      /^(.+?): .*$/,
      '$1: Verwenden Sie ein Design-Token wie var(--accent) statt eines Farbwerts oder -namens',
    );
  if (m.includes('urls, expressions and !important'))
    return m.replace(/^(.+?): .*$/, '$1: URLs, Ausdrücke und !important sind nicht erlaubt');
  if ((r = /^(.+?): the function (.+)\(\) is not allowed$/.exec(m)))
    return `${r[1]}: Die Funktion ${r[2]}() ist nicht erlaubt`;
  if (m.includes('content may only be a text')) return 'content darf nur ein Text sein';
  if (m.startsWith('This selector is not allowed')) return 'Dieser Selektor ist nicht erlaubt';
  if (m.startsWith('A keyframe step'))
    return 'Ein Keyframe-Schritt muss from, to oder eine Prozentangabe sein';
  if ((r = /^The query "(.+)" is not allowed/.exec(m)))
    return `Die Abfrage „${r[1]}“ ist nicht erlaubt (nur Breite, Höhe, prefers-reduced-motion, prefers-color-scheme und orientation)`;
  if ((r = /^(@\S+) is not allowed$/.exec(m))) return `${r[1]} ist nicht erlaubt`;
  if (m.startsWith('The markup is longer')) return 'Das Markup ist zu lang';
  if (m.startsWith('The stylesheet is longer')) return 'Das Stylesheet ist zu lang';
  if (m.startsWith('At most'))
    return m
      .replace('At most', 'Höchstens')
      .replace('elements', 'Elemente')
      .replace('levels deep', 'Ebenen tief');
  return m;
}
